import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  authApi,
  fitnessApi,
  gamificationApi,
  pathwayApi,
  peopleApi,
  type WeeklyMissionPayload,
} from '../../api';
import { getApiBaseUrl } from '../../config';
import { missionPayloadFrom, MISSION_ACTIVITY_EVENT } from '../../missionEvents';
import type { Person } from '../../types/person';

export function useApplicationData(isAuthenticated: boolean, userId?: number) {
  const [people, setPeople] = useState<Person[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fitnessSummary, setFitnessSummary] = useState<any>(null);
  const [mindStats, setMindStats] = useState<any>(null);
  const [pathwayDashboard, setPathwayDashboard] = useState<any>(null);
  const [dimensionScores, setDimensionScores] = useState<Record<string, number>>({});
  const [weeklyMission, setWeeklyMission] = useState<WeeklyMissionPayload | null>(null);
  const [missionLoading, setMissionLoading] = useState(true);
  const [missionError, setMissionError] = useState<string | null>(null);
  const [gameStatsError, setGameStatsError] = useState<string | null>(null);
  const statsRequestSequence = useRef(0);
  const peopleRequestSequence = useRef(0);
  const bestPathway = React.useMemo(() => {
    const progress: any[] = pathwayDashboard?.user_progress || [];
    const pws: any[] = pathwayDashboard?.pathways || [];
    if (!progress.length) return null;
    const best = progress.reduce(
      (a: any, b: any) => (a.current_sequence <= b.current_sequence ? a : b),
      progress[0],
    );
    const pw = pws.find((p: any) => p.id === best.pathway_id);
    if (!pw) return null;
    const seq = (pw.sequences || []).find((s: any) => s.level === best.current_sequence);
    return {
      name: pw.name,
      icon: pw.icon,
      level: best.current_sequence,
      levelName: seq?.name || `第${best.current_sequence}级`,
    };
  }, [pathwayDashboard]);

  const normalizeFitnessSummary = (payload: any) => {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.hp || payload.ep) return payload;
    if (payload.summary && (payload.summary.hp || payload.summary.ep)) return payload.summary;
    if (payload.data && (payload.data.hp || payload.data.ep)) return payload.data;
    if (payload.hp_data || payload.ep_data) {
      return {
        hp: payload.hp_data || null,
        ep: payload.ep_data || null,
      };
    }
    return null;
  };

  const applyWeeklyMission = useCallback((mission: WeeklyMissionPayload) => {
    const scores: Record<string, number> = {};
    (mission.groups || []).forEach((group) => {
      if (group.key && Number.isFinite(Number(group.score)))
        scores[group.key] = Number(group.score);
    });
    setDimensionScores(scores);
    setWeeklyMission(mission);
    setMissionError(null);
    setMissionLoading(false);
  }, []);

  const acceptMissionUpdate = useCallback(
    (mission: WeeklyMissionPayload) => {
      // A mutation result is newer than any aggregate request already in flight.
      statsRequestSequence.current += 1;
      applyWeeklyMission(mission);
    },
    [applyWeeklyMission],
  );

  /** 健身 HP、EP 与途径面板；各路独立，避免单个接口失败导致顶部状态为空 */
  const loadGameStats = useCallback(async () => {
    const sequence = ++statsRequestSequence.current;
    const [fitRes, mindRes, pathRes, missionRes] = await Promise.allSettled([
      fitnessApi.getSummary(),
      authApi.getSanStats(),
      pathwayApi.getDashboard(),
      gamificationApi.getWeeklyMission().then((response) => {
        if (sequence === statsRequestSequence.current) applyWeeklyMission(response.data);
        return response;
      }),
    ]);
    if (sequence !== statsRequestSequence.current) return;
    let hasFitnessData = false;
    let hasMindData = false;
    if (fitRes.status === 'fulfilled') {
      const normalized = normalizeFitnessSummary(fitRes.value?.data);
      if (normalized) {
        setFitnessSummary(normalized);
        console.debug('[HP/energyValue] summary loaded:', normalized);
        hasFitnessData = true;
      } else {
        setFitnessSummary(null);
        setGameStatsError('HP/EP 数据格式异常');
        console.error('健身总览返回结构异常:', fitRes.value?.data);
      }
    } else {
      setFitnessSummary(null);
      setGameStatsError('HP 数据加载失败');
      console.error('加载健身总览失败:', fitRes.reason);
    }
    if (mindRes.status === 'fulfilled') {
      setMindStats(mindRes.value.data);
      hasMindData = true;
    } else {
      setMindStats(null);
      setGameStatsError('EP 数据加载失败');
      console.error('加载EP失败:', mindRes.reason);
    }
    if (pathRes.status === 'fulfilled') {
      setPathwayDashboard(pathRes.value.data);
    } else {
      console.error('加载途径总览失败:', pathRes.reason);
    }
    if (missionRes.status === 'fulfilled') {
      applyWeeklyMission(missionRes.value.data);
    } else {
      setMissionError('本周任务加载失败');
      console.error('加载六维分数失败:', missionRes.reason);
    }
    setMissionLoading(false);
    if (hasFitnessData && hasMindData) setGameStatsError(null);
  }, [applyWeeklyMission]);

  useEffect(() => {
    if (!isAuthenticated) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onActivity = (event: Event) => {
      const mission = missionPayloadFrom((event as CustomEvent).detail?.mission);
      if (mission) acceptMissionUpdate(mission);
      else statsRequestSequence.current += 1;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        loadGameStats();
      }, 120);
    };
    window.addEventListener(MISSION_ACTIVITY_EVENT, onActivity);
    return () => {
      window.removeEventListener(MISSION_ACTIVITY_EVENT, onActivity);
      if (timer) clearTimeout(timer);
      statsRequestSequence.current += 1;
    };
  }, [isAuthenticated, acceptMissionUpdate, loadGameStats]);

  const loadPeople = useCallback(async () => {
    const sequence = ++peopleRequestSequence.current;
    try {
      setLoading(true);
      const response = await peopleApi.getAll();
      if (sequence !== peopleRequestSequence.current) return;
      setPeople(response.data || []);
      setError(null);
    } catch (err: any) {
      if (sequence !== peopleRequestSequence.current) return;
      console.error('加载人员列表失败:', err);
      const errorMsg = err.response?.data?.detail || err.message || '无法连接到服务器';
      setError('加载失败: ' + errorMsg);
      setPeople([]);
    } finally {
      if (sequence === peopleRequestSequence.current) setLoading(false);
    }
  }, []);

  const isNetworkError = error?.includes('Network Error');
  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const useLanHint = currentHost !== '' && !['localhost', '127.0.0.1', '::1'].includes(currentHost);
  const recommendedApiUrl = useLanHint
    ? `${window.location.protocol}//${currentHost}:8000`
    : getApiBaseUrl();
  const errorHint = isNetworkError
    ? `请确认后端已启动，并点击「设置」检查 API 地址是否为 ${recommendedApiUrl}`
    : null;

  useEffect(() => {
    setPeople([]);
    setError(null);
    setFitnessSummary(null);
    setMindStats(null);
    setPathwayDashboard(null);
    setDimensionScores({});
    setWeeklyMission(null);
    setGameStatsError(null);
    setMissionError(null);
    setMissionLoading(isAuthenticated);
    setLoading(isAuthenticated);
    if (isAuthenticated) {
      loadPeople();
      loadGameStats();
    }
    return () => {
      statsRequestSequence.current += 1;
      peopleRequestSequence.current += 1;
    };
  }, [isAuthenticated, userId, loadGameStats, loadPeople]);

  // 从后台回到前台时刷新 HP/EP（防抖，避免部分环境下 visibility 抖动导致请求暴增）
  useEffect(() => {
    if (!isAuthenticated) return;
    let t: ReturnType<typeof setTimeout> | null = null;
    const onVis = () => {
      if (document.visibilityState !== 'visible') return;
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        t = null;
        loadGameStats();
      }, 400);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      if (t) clearTimeout(t);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [isAuthenticated, loadGameStats]);

  const lastGameStatsFetchRef = useRef(0);
  const refreshGameStatsThrottled = useCallback(() => {
    const now = Date.now();
    if (now - lastGameStatsFetchRef.current < 1500) return;
    lastGameStatsFetchRef.current = now;
    loadGameStats();
  }, [loadGameStats]);

  // 定时兜底刷新，避免某些端上生命周期事件丢失导致顶部数值不更新
  useEffect(() => {
    if (!isAuthenticated) return;
    const timer = setInterval(() => {
      refreshGameStatsThrottled();
    }, 20000);
    return () => clearInterval(timer);
  }, [isAuthenticated, refreshGameStatsThrottled]);

  const hpRaw = Number(fitnessSummary?.hp?.hp);
  const mindEpRaw = Number(mindStats?.mind_ep);
  const healthHp = Number.isFinite(hpRaw) ? Math.max(0, Math.min(100, Math.round(hpRaw))) : null;
  const mindEp = Number.isFinite(mindEpRaw)
    ? Math.max(0, Math.min(100, Math.round(mindEpRaw)))
    : null;
  const healthPct = healthHp ?? 0;
  const mindPct = mindEp ?? 0;
  return {
    people,
    loading,
    error,
    fitnessSummary,
    setFitnessSummary,
    mindStats,
    pathwayDashboard,
    dimensionScores,
    weeklyMission,
    missionLoading,
    missionError,
    gameStatsError,
    bestPathway,
    acceptMissionUpdate,
    loadGameStats,
    loadPeople,
    errorHint,
    healthHp,
    mindEp,
    healthPct,
    mindPct,
    refreshGameStatsThrottled,
  };
}
